/**
 * Markdown (lecture seule) -> HTML : commentaires d'objet et réponses de l'assistant IA.
 * Tout le texte est échappé : seul le HTML produit par ce rendu passe. `inlineMarkdown` sert aussi à
 * eva-markdown-editor, qui écrit ce HTML directement dans le DOM (sans sanitizer).
 */
export function renderMarkdown(md: string): string {
  if (!md) return "";
  const lines = md.split("\n");
  const result: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.trimStart().startsWith("```")) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trimStart().startsWith("```")) {
        codeLines.push(escapeHtml(lines[i]));
        i++;
      }
      i++;
      result.push(`<pre><code>${codeLines.join("\n") || "<br>"}</code></pre>`);
      continue;
    }

    const heading = line.match(/^(#{1,4}) (.*)$/);
    if (heading) {
      // Titres ramenés à h2-h4 : un h1 dans une bulle de chat écraserait la hiérarchie de la page.
      const level = Math.min(Math.max(heading[1].length, 2), 4);
      result.push(`<h${level}>${inlineMarkdown(heading[2])}</h${level}>`);
      i++;
      continue;
    }

    if (line.startsWith("> ")) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].startsWith("> ")) {
        quoteLines.push(inlineMarkdown(lines[i].substring(2)));
        i++;
      }
      result.push(`<blockquote>${quoteLines.join("<br>")}</blockquote>`);
      continue;
    }

    if (isTableRow(line) && i + 1 < lines.length && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1])) {
      const header = tableCells(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && isTableRow(lines[i])) {
        rows.push(tableCells(lines[i]));
        i++;
      }
      const head = header.map((c) => `<th>${inlineMarkdown(c)}</th>`).join("");
      const body = rows.map((r) => `<tr>${r.map((c) => `<td>${inlineMarkdown(c)}</td>`).join("")}</tr>`).join("");
      result.push(`<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`);
      continue;
    }

    if (/^\s*[-*+] /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*+] /.test(lines[i])) {
        items.push(`<li>${inlineMarkdown(lines[i].replace(/^\s*[-*+] /, ""))}</li>`);
        i++;
      }
      result.push(`<ul>${items.join("")}</ul>`);
      continue;
    }

    if (/^\s*\d+\. /.test(line)) {
      // Liste coupée (ligne vide, paragraphe) : elle reprend à son numéro, pas à 1.
      const start = parseInt(line.trim(), 10);
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\. /.test(lines[i])) {
        const item = inlineMarkdown(lines[i].replace(/^\s*\d+\. /, ""));
        i++;
        // Puces indentées sous un point numéroté : dans ce point, sinon elles fermaient la liste.
        const sub: string[] = [];
        while (i < lines.length && /^\s{2,}[-*+] /.test(lines[i])) {
          sub.push(`<li>${inlineMarkdown(lines[i].replace(/^\s*[-*+] /, ""))}</li>`);
          i++;
        }
        items.push(`<li>${item}${sub.length ? `<ul>${sub.join("")}</ul>` : ""}</li>`);
      }
      result.push(`<ol${start > 1 ? ` start="${start}"` : ""}>${items.join("")}</ol>`);
      continue;
    }

    if (line.trim() === "") {
      i++;
      continue;
    }

    result.push(`<p>${inlineMarkdown(line)}</p>`);
    i++;
  }

  return result.join("");
}

function isTableRow(line: string): boolean {
  return /^\s*\|.*\|\s*$/.test(line);
}

function tableCells(line: string): string[] {
  return line
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((c) => c.trim());
}

/**
 * Liens de l'assistant IA vers un objet EVA (`eva://offre/<id>`) -> page de l'application.
 * shortcut: recopié de EVA-AGENT (links.py ROUTES) et dans EVA-MOBILE ; à garder aligné à la main.
 */
const EVA_ROUTES: Record<string, string> = {
  chantier: "projects",
  offre: "offers",
  facture: "invoices/issued",
  client: "customers",
  fournisseur: "suppliers",
  intervention: "interventions",
  bon_commande: "purchase-orders",
  collaborateur: "employees",
};

export function inlineMarkdown(text: string): string {
  if (!text) return "";
  const codeSegments: string[] = [];
  let processed = text.replace(/`([^`]+)`/g, (_, code) => {
    const idx = codeSegments.length;
    codeSegments.push(`<code>${escapeHtml(code)}</code>`);
    return `\x00CODE${idx}\x00`;
  });
  // Texte de l'IA ou d'un utilisateur : aucune balise ne passe, sinon un <img src="https://…">
  // injecté via une donnée EVA ferait sortir des informations sans aucun clic.
  processed = escapeHtml(processed);
  processed = processed.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  processed = processed.replace(/__(.+?)__/g, "<strong>$1</strong>");
  processed = processed.replace(/\*(.+?)\*/g, "<em>$1</em>");
  processed = processed.replace(/(?<!\w)_(.+?)_(?!\w)/g, "<em>$1</em>");
  // javascript:, data:… restent du texte : l'éditeur écrit ce HTML sans passer par le sanitizer Angular.
  processed = processed.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (link, label, url) => {
    const eva = /^eva:\/\/([a-z_]+)\/([0-9a-f]{24})$/.exec(url);
    if (eva) return EVA_ROUTES[eva[1]] ? `<a href="#/${EVA_ROUTES[eva[1]]}/${eva[2]}">${label}</a>` : label;
    return /^\s*(?!https?:|mailto:|tel:)[a-z][\w+.-]*:/i.test(url)
      ? link
      : `<a href="${url}" target="_blank">${label}</a>`;
  });
  processed = processed.replace(/\x00CODE(\d+)\x00/g, (_, idx) => codeSegments[parseInt(idx, 10)]);
  return processed;
}

export function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
